<script>
import { computed, ref } from "vue";
import store from '@/store'

export default {
  name: "PProductQuantityPicker",
  props: {
    allowNegativeVolume: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    hidePricesSveCatC: { type: Boolean, default: false },
    isExternal: { type: Boolean, default: false },
    isGL: { type: Boolean, default: false },
    isHE: { type: Boolean, default: false },
    isTO: { type: Boolean, default: false },
    product: { type: Object, required: true },
    showBalances: { type: Boolean, default: true },
    showFill: { type: Boolean, default: true },
    showInternalStock: { type: Boolean, default: false },
    showPrices: { type: Boolean, default: true },
    wrongStepError: { type: String, default: 'Du kan ikke velge antall som ikke er et multiplum av {x}' }
  },
  inject: ["appConfig", 'tk'],
  emits: ["updateProduct"],
  setup(props) {
    const { loginUser } = store.getters
    const error = ref('')

    // https://dev.azure.com/smartreturdev/Portal/_workitems/edit/38/
    // https://dev.azure.com/smartreturdev/Portal/_workitems/edit/104/ - always use product.qtyMax, correct max is set in api
    // const productMax = computed(() => {
    //   let max = !props.product.qtyFree ? props.product.qtyMaxOrder : 9999;
    //   if (props.isTO && props.showInternalStock && props.product.isCommission) {
    //     const qtyStock = props.product.qtyStock * -1;
    //     max = qtyStock < 0 ? 0 : qtyStock >= 0 && qtyStock < max ? qtyStock : max;
    //   }
    //   return max;
    // })
    const productMin = computed(() =>
      props.allowNegativeVolume && props.product.qtyFree ? -9999 : props.product.quantityExtra
    )
    const productStep = computed(() => props.product.qtyFree ? 1 : props.product.qtyPick || 1)
    const productClasses = computed(() => {
      const classes = ["w-full", "px-3", "py-2", "mt-1", "mb-2", "border", "rounded-md"];
      classes.push(props.product.quantity ? 'bg-green-100 border-green-500' : 'bg-gradient-to-b from-gray-50 to-gray-200  border-gray-300')

      if (props.product.qtyMax === 0 && productMin.value === 0 || props.isLoading)
        classes.push('opacity-50')

      return classes
    })

    return {
      loginUser, productClasses, productMin, productStep, error
    }
  },
  methods: {
    setQuantity100() {
      if (!this.product.isBlocked) {
        let quantity = this.product.qtyMaxOrder;

        // https://dev.azure.com/smartreturdev/Portal/_workitems/edit/38/
        if (
          this.isTO &&
          this.showInternalStock &&
          this.product.isCommission
        ) {
          const qtyStock = this.product.qtyStock * -1;
          quantity =
            qtyStock < 0
              ? 0
              : qtyStock >= 0 && qtyStock < quantity
                ? qtyStock
                : quantity;
        }

        // "snap" to highest possible
        if (!this.product.qtyFree && quantity > 0 && this.product.qtyPick > 0) {
          quantity = Number.parseInt(quantity / this.product.qtyPick, 10) * this.product.qtyPick;
        }
        this.setQuantity(quantity);
      }
    },
    setQuantity(quantity) {
      this.error = '';
      this.$emit("updateProduct", this.product, quantity);
    },
    numberFormat(number, decimals) {
      return new Intl.NumberFormat(this.$i18n.locale.replace('-sw', ''), {
        minimumFractionDigits: decimals,
      }).format(number);
    },
    setWrongStepError() {
      this.error = this.wrongStepError.replace('{x}', this.productStep)
      // setTimeout(() => { // *** remove error after 5 sek.
      //   this.error = '';
      // }, 5000);
    }
  }
}
</script>

<template>
  <div :class="productClasses">
    <div class="flex flex-row flex-wrap gap-2 md:gap-4">
      <div class="flex flex-row flex-grow gap-2">
        <img
          alt=""
          :src="`${appConfig.assetsBaseUrl}/media.ashx?${product.image}&template=product-image-69`"
        />
        <div class="my-auto text-left">
          <template v-if="isHE && product.qualityId === 'SORT'">
            <div class="font-medium">
              {{ product.qualityName }}
            </div>
          </template>

          <template v-else>
            <div class="font-medium">
              {{ product.productName }}
            </div>

            <div
              class="text-xs"
              v-if="!isExternal && (isGL && product.isPooling) === false"
            >
              {{ product.qualityName }}
              {{ product.treatmentIsDefault ? "" : product.treatmentName }}
            </div>
          </template>
        </div>
      </div>
      <div class="flex flex-row justify-end flex-1 gap-2 my-auto ml-auto mr-0">
        <div
          v-if="product.qtyStockType === 'Receipt'"
          class="my-auto text-center"
        >
          <div class="font-medium">
            <PTranslation k="Common.General.PalletReceipt" />
          </div>
          <div class="text-sm">
            <PTranslation k="Common.General.qtyStock" />:
            {{ product.qtyStock }}
          </div>
        </div>

        <div
          class="my-auto text-center"
          v-else-if="showBalances && product.isCommission"
        >
          <div class="font-medium">
            <PTranslation k="Common.General.Commission" />
          </div>
          <div
            v-if="product.qtyStock !== null"
            :class="['text-sm my-auto', { 'text-green-500': (showInternalStock && product.qtyStock < 0) || (!showInternalStock && product.qtyStock > 0), 'text-red-500': (showInternalStock && product.qtyStock > 0) || (!showInternalStock && product.qtyStock < 0), }]"
          >
            <template v-if="
              (showInternalStock && product.qtyStock < 0) ||
              (!showInternalStock && product.qtyStock > 0)
            ">
              <span class="font-semibold">
                {{ product.qtyStock * (showInternalStock ? -1 : 1) }}
              </span>
              <span>
                {{ tk("Common.General.Credited").toLowerCase() }}
              </span>
              <span
                class="text-red-500"
                v-if="product.noNegComm && product.qtyMax < product.qtyMaxOrder"
              >
                ({{ tk("Common.General.Max").toLowerCase() }})
              </span>
            </template>
            <template v-else-if="
              (showInternalStock && product.qtyStock > 0) ||
              (!showInternalStock && product.qtyStock < 0)
            ">
              <span class="font-semibold">{{
                product.qtyStock * (showInternalStock ? 1 : -1)
              }}</span>
              <span> {{ tk('Common.General.Owes').toLowerCase() }}</span>
            </template>
            <template v-else>
              <span v-html="tk('Common.General.Balance')"></span>:
              <span class="font-semibold">{{ product.qtyStock }}</span>
            </template>
          </div>
        </div>

        <div
          class="flex items-baseline my-auto text-lg"
          v-if="!hidePricesSveCatC && showPrices && product.price > 0"
        >
          <span class="text-xs">{{ product.currencyId }}</span>
          <span class="ml-1 font-semibold">{{
            numberFormat(product.price, 2)
          }}</span>
        </div>

        <div
          v-if="hidePricesSveCatC"
          class='my-auto '
        >
          <div class="inline-flex items-center">
            <span class="text-xs">Prisuppgift</span>
            <PTooltip
              class="ml-1 text-xs"
              :content="'Ta kontakt med order@smartretur.se'"
            />
          </div>
        </div>

        <div
          class="my-auto leading-none text-center"
          :class="{ invisible: product.isBlocked }"
        >
          <template v-if="!product.qtyFree && product.qtyPick > 1">
            &#177;{{ product.qtyPick }}
          </template>
        </div>

        <template v-if="!product.isBlocked || loginUser.isSmartRetur">
          <div class="relative my-auto mt-2 md:mt-0">
            <div
              v-if="product.quantityExtra > 0"
              class="absolute z-10 mt-1 text-xs text-red-500 top-2 left-2"
              v-tooltip="{
                content: $t('Common.General.OfWhichUnderOtherProducts', {
                  count: product.quantityExtra,
                  name: product.productName.toLowerCase(),
                }),
                delay: {
                  show: 100,
                  hide: 100,
                },
              }"
            >
              <FontAwesomeIcon :icon="['fad', 'pallet-alt']" />
              {{ product.quantityExtra }}
            </div>

            <div
              v-if="product.isBlocked"
              class="absolute z-10 mt-1 text-xs text-red-500 top-2"
              :class="{
                'left-10': product.quantityExtra > 0,
                'left-2': !product.quantityExtra,
              }"
              v-tooltip="{
                content: $t('OrderDialog.NotAvailableButAsSRYouCanOrder'),
                delay: {
                  show: 100,
                  hide: 100,
                },
              }"
            >
              <FontAwesomeIcon :icon="['far', 'exclamation-triangle']" />
            </div>

            <PNumber
              :disabled="disabled || (product.qtyMax === 0 && productMin === 0)"
              :extra="product.quantityExtra"
              :max="product.qtyMax"
              :min="productMin"
              :step="productStep"
              :value="product.quantity"
              :wait="1000"
              :warn="product.qtyWarn"
              :warnMessage="$t('OrderDialog.WarningHighVolume')"
              class="w-28"
              @input="setQuantity"
              @wrongStepValue="setWrongStepError"
            />
          </div>

          <div class="flex items-center gap-4 p-2 my-auto md:p-0">
            <button
              v-if="showFill"
              :class="['text-sm underline', {
                'cursor-not-allowed': disabled,
                invisible: product.qtyMaxOrder === 0,
              }]"
              :disabled="disabled"
              type="button"
              @click.prevent="setQuantity100"
            ><span v-html="tk('Common.General.Fill')" /></button>

            <button
              :class="['text-sm underline', {
                'cursor-not-allowed': disabled || product.quantity === 0,
                'text-red-500': product.quantity > 0,
              }]"
              :disabled="disabled || product.quantity === 0"
              title="Tøm dette produktet fra bil"
              @click.prevent="() => setQuantity(0)"
            >
              <FontAwesomeIcon :icon="['far', 'trash-alt']" />
            </button>
          </div>
        </template>

        <template v-else>
          <slot
            name="blocked"
            :product="product"
          >
            <div class="text-sm text-center">
              <PTranslation k="Common.General.NotAvailable" />
            </div>
          </slot>
        </template>
      </div>
    </div>
    <p
      v-if="error"
      class="p-2 text-sm text-right text-red-500"
    >{{ error }}</p>
  </div>
</template>
